import {
  DEFAULT_TENANT_UI_CONFIG,
  LTS_ANALYSIS_MODE_IDS,
  LtsAnalysisModeId,
  SidebarLogoOverride,
  TenantUiConfig,
} from '@/shared/constants/custom-rendering-accounts';
import type {
  SpaceUiLtsFeatureConfigResType,
  SpaceUiProfileResType,
  SpaceUiSidebarLogoResType,
} from '@/shared/apis/space/get-space-ui-profile';

/**
 * space UI 프로파일 serve delta 를 base(tenant 중립 DEFAULT) 위에 재구성해 TenantUiConfig 를 만든다.
 *
 * 원칙(플랜 §0.3 base+override):
 *  - 화면UI 키(coin/payment/customHome ...)는 uiConfig 에서 명시된 것만 덮고 나머지는 base.
 *  - 홈 카드 노출은 homeMenus delta 행이 있으면 그 enabled, 없으면 base(구 DEFAULT 극성).
 *  - lts 세부는 lts.highlight 메뉴 featureConfig 에서, sentinel('none'/'auto')은 FE 의 null 로 환원.
 * 이 재구성은 be-api seed 검증(equivalence gate)과 동일한 매핑이라 시드된 space 는 (제거된) 구 tenant 상수 매핑과 동치.
 */
export function buildTenantUiConfigFromProfile(
  profile: SpaceUiProfileResType,
  base: TenantUiConfig,
): TenantUiConfig {
  const ui = profile.uiConfig ?? {};
  const menuByCatalog = new Map(
    profile.homeMenus.map((m) => [m.catalogItemId, m]),
  );
  const ltsFc = menuByCatalog.get('lts.highlight')?.featureConfig ?? {};

  const enabledOf = (catalogItemId: string, fallback: boolean): boolean => {
    const row = menuByCatalog.get(catalogItemId);
    return row ? row.enabled : fallback;
  };

  // 홈 카드 노출 플래그 (파생 showOnlyLts 계산에 재사용)
  const showLtsButton = enabledOf('lts.highlight', base.showLtsButton);
  const showMtsButton = enabledOf('mts.script', base.showMtsButton);
  const showGtsChip = enabledOf('gts', base.showGtsChip);
  const showImageEditor = enabledOf('image-editor', base.showImageEditor);
  const showInfoVideoWizard = enabledOf('info-video', base.showInfoVideoWizard);
  const showBroadcastVaultButton = enabledOf(
    'broadcast-vault',
    base.showBroadcastVaultButton,
  );
  // showOnlyLts 는 저장하지 않고 파생한다(구 매핑 "showOnlyLts → mts·image off" 의 역):
  // lts 만 노출되고 다른 홈 카드가 모두 꺼져 있으면 only-lts.
  const showOnlyLts =
    showLtsButton &&
    !showMtsButton &&
    !showImageEditor &&
    !showGtsChip &&
    !showInfoVideoWizard &&
    !showBroadcastVaultButton;

  return {
    ...base,
    // ── 화면UI (uiConfig delta) ──
    useCustomHome: pick(ui.useCustomHome, base.useCustomHome),
    redirectHome: pick(ui.redirectHome, base.redirectHome),
    showHomeGuide: pick(ui.showHomeGuide, base.showHomeGuide),
    showHomeSubCategories: pick(
      ui.showHomeSubCategories,
      base.showHomeSubCategories,
    ),
    duplicateDialogIsGsr: pick(
      ui.duplicateDialogIsGsr,
      base.duplicateDialogIsGsr,
    ),
    showStcChip: pick(ui.showStcChip, base.showStcChip),
    showCoin: pick(ui.showCoin, base.showCoin),
    showCoinCharge: pick(ui.showCoinCharge, base.showCoinCharge),
    showPlanUpgrade: pick(ui.showPlanUpgrade, base.showPlanUpgrade),
    showPaymentMenu: pick(ui.showPaymentMenu, base.showPaymentMenu),
    showPaymentAlert: pick(ui.showPaymentAlert, base.showPaymentAlert),
    showEmail: pick(ui.showEmail, base.showEmail),
    showSpaceManagement: pick(ui.showSpaceManagement, base.showSpaceManagement),
    showAccountSelfService: pick(
      ui.showAccountSelfService,
      base.showAccountSelfService,
    ),
    showBetaLabel: pick(ui.showBetaLabel, base.showBetaLabel),
    // LTS 업로드 화면의 플랜 용량 제한 안내 박스 노출 여부(uiConfig delta).
    // 구 tenant 상수(GSR/GS=off)를 대체 — 미설정 시 base(true).
    showLtsUploadLimitInfo: pick(
      ui.showLtsUploadLimitInfo,
      base.showLtsUploadLimitInfo,
    ),
    // GS 전용 모드(uiConfig delta). 구 isGsrAccount(tenantId) 하드코딩 대체 — 미설정 시 base(false).
    gsExclusive: pick(ui.gsExclusive, base.gsExclusive),

    // ── 기능 프로필 게이트 ──
    exportFpsSelectionEnabled: profile.exportFpsSelectionEnabled,

    // ── 메뉴 노출 (homeMenus delta → base 극성 폴백) ──
    showLtsButton,
    showMtsButton,
    showGtsChip,
    showImageEditor,
    showInfoVideoWizard,
    showBroadcastVaultButton,
    showOnlyLts,

    // ── LTS 부가기능 — serve ltsFeatureConfig(BE 합성 완료) 우선, 부재 시 구 featureConfig 경로 ──
    ...(profile.ltsFeatureConfig
      ? mapServeLtsFeatureConfig(profile.ltsFeatureConfig, base)
      : {
          showLtsModeStep: pick(ltsFc.showLtsModeStep, base.showLtsModeStep),
          showLtsMultimodalMode: pick(
            ltsFc.showLtsMultimodalMode,
            base.showLtsMultimodalMode,
          ),
          showPortraitMode: pick(ltsFc.showPortraitMode, base.showPortraitMode),
          showLtsAnalysisFilters: pick(
            ltsFc.showLtsAnalysisFilters,
            base.showLtsAnalysisFilters,
          ),
          ltsDefaultIndexLevel: pick(
            ltsFc.ltsDefaultIndexLevel,
            base.ltsDefaultIndexLevel,
          ),
          uploadIndexLevel: pick(ltsFc.uploadIndexLevel, base.uploadIndexLevel),
          ltsLockedModelId: mapSentinel(
            ltsFc.ltsLockedModelId,
            'none',
            base.ltsLockedModelId,
          ),
          ltsDefaultModelId: mapSentinel(
            ltsFc.ltsDefaultModelId,
            'auto',
            base.ltsDefaultModelId,
          ),
          ltsButtonI18nKey: pick(ltsFc.ltsButtonI18nKey, base.ltsButtonI18nKey),
          // 구버전 서버: 모드 목록 미제공(null) — 소비처가 개별 show* 플래그 + 미드폼 tenant 게이트로 폴백
          ltsSupportedModes: base.ltsSupportedModes,
          // 재필터 게이트는 구버전에서 showLtsAnalysisFilters 였다 — 동등 폴백(신 featureConfig 키 우선)
          commerceEnabled: pick(
            ltsFc.commerceEnabled,
            pick(ltsFc.showLtsAnalysisFilters, base.showLtsAnalysisFilters),
          ),
          forceAdvancedOnStaticFilter: base.forceAdvancedOnStaticFilter,
          // 구버전 서버에도 lts.highlight featureConfig 로 space 단위 켜기가 가능하다(미설정 = base false)
          showLtsCaptionTranslation: pick(
            ltsFc.captionTranslationEnabled,
            base.showLtsCaptionTranslation,
          ),
        }),

    // ── 사이드바 로고 ──
    sidebarLogo: mapSidebarLogo(profile.sidebarLogo, base.sidebarLogo),
  };
}

/**
 * serve ltsFeatureConfig(프로필 기본값 + space override 를 BE 가 합성 완료) → TenantUiConfig LTS 필드.
 * PLAYGROUND 랜딩 space 도 값을 받는다(구 homeMenus 경로의 구조적 한계 해소).
 * showPortraitMode/showLtsMultimodalMode 는 supportedModes 파생값으로 유지(기존 소비처 호환).
 */
function mapServeLtsFeatureConfig(
  serve: SpaceUiLtsFeatureConfigResType,
  base: TenantUiConfig,
): Pick<
  TenantUiConfig,
  | 'showLtsModeStep'
  | 'showLtsMultimodalMode'
  | 'showPortraitMode'
  | 'showLtsAnalysisFilters'
  | 'ltsDefaultIndexLevel'
  | 'uploadIndexLevel'
  | 'ltsLockedModelId'
  | 'ltsDefaultModelId'
  | 'ltsButtonI18nKey'
  | 'ltsSupportedModes'
  | 'commerceEnabled'
  | 'forceAdvancedOnStaticFilter'
  | 'showLtsCaptionTranslation'
> {
  const filteredModes = LTS_ANALYSIS_MODE_IDS.filter((id) =>
    serve.supportedModes.includes(id),
  );
  // 빈 목록은 "미설정" 으로 본다(BE 도 프로필 값으로 복귀) — 전 모드 숨김 후 light 강제 제출 → 400 방지
  const supportedModes =
    filteredModes.length > 0
      ? filteredModes
      : (base.ltsSupportedModes ?? LTS_ANALYSIS_MODE_IDS);
  return {
    showLtsModeStep: serve.showModeStep,
    showLtsMultimodalMode: supportedModes.includes('multimodal'),
    showPortraitMode: supportedModes.includes('portrait'),
    showLtsAnalysisFilters: serve.showAnalysisFilters,
    ltsDefaultIndexLevel: serve.defaultIndexLevel ?? base.ltsDefaultIndexLevel,
    uploadIndexLevel: serve.uploadIndexLevel ?? base.uploadIndexLevel,
    ltsLockedModelId: asModeId(serve.lockedModeId),
    ltsDefaultModelId: asModeId(serve.defaultModeId),
    ltsButtonI18nKey: serve.buttonI18nKey ?? base.ltsButtonI18nKey,
    ltsSupportedModes: supportedModes,
    // 미명시(null) = 구 재필터 게이트였던 showAnalysisFilters 동등 — 시드 전 GS space 무회귀
    commerceEnabled: serve.commerceEnabled ?? serve.showAnalysisFilters,
    forceAdvancedOnStaticFilter: serve.forceAdvancedOnStaticFilter,
    showLtsCaptionTranslation: serve.captionTranslationEnabled,
  };
}

function asModeId(value: string | null): LtsAnalysisModeId | null {
  return value && LTS_ANALYSIS_MODE_IDS.includes(value as LtsAnalysisModeId)
    ? (value as LtsAnalysisModeId)
    : null;
}

/**
 * account 14744 등 LTS UI 를 tenant/space 무관 DEFAULT 로 강제하는 오버라이드(§1.6 유지).
 * (제거된) 구 getTenantUiConfig 의 override 와 동일 필드 집합.
 */
export function applyLtsDefaultUiOverride(
  config: TenantUiConfig,
): TenantUiConfig {
  return {
    ...config,
    showLtsButton: DEFAULT_TENANT_UI_CONFIG.showLtsButton,
    showLtsModeStep: DEFAULT_TENANT_UI_CONFIG.showLtsModeStep,
    showLtsMultimodalMode: DEFAULT_TENANT_UI_CONFIG.showLtsMultimodalMode,
    ltsDefaultIndexLevel: DEFAULT_TENANT_UI_CONFIG.ltsDefaultIndexLevel,
    showLtsUploadLimitInfo: DEFAULT_TENANT_UI_CONFIG.showLtsUploadLimitInfo,
    ltsLockedModelId: DEFAULT_TENANT_UI_CONFIG.ltsLockedModelId,
    showPortraitMode: DEFAULT_TENANT_UI_CONFIG.showPortraitMode,
    ltsDefaultModelId: DEFAULT_TENANT_UI_CONFIG.ltsDefaultModelId,
    ltsButtonI18nKey: DEFAULT_TENANT_UI_CONFIG.ltsButtonI18nKey,
    ltsSupportedModes: DEFAULT_TENANT_UI_CONFIG.ltsSupportedModes,
    forceAdvancedOnStaticFilter:
      DEFAULT_TENANT_UI_CONFIG.forceAdvancedOnStaticFilter,
    showLtsCaptionTranslation:
      DEFAULT_TENANT_UI_CONFIG.showLtsCaptionTranslation,
  };
}

/** serve profile 이 "설정된 space"(신 데이터)인지 판정. 하나라도 delta 가 있으면 신 시스템. */
export function hasSpaceUiProfile(profile: SpaceUiProfileResType): boolean {
  return (
    profile.homeMenus.length > 0 ||
    profile.playgroundMenus.length > 0 ||
    profile.uiConfig !== null ||
    profile.sidebarLogo !== null ||
    profile.landingDestination !== null
  );
}

function pick<T>(value: unknown, fallback: T): T {
  return value === undefined || value === null ? fallback : (value as T);
}

/** sentinel('none'/'auto')은 FE 의 null 로 환원, 미설정은 base, 그 외는 값. */
function mapSentinel<T>(
  value: unknown,
  sentinel: string,
  fallback: T,
): T | null {
  if (value === undefined || value === null) return fallback;
  if (value === sentinel) return null;
  return value as T;
}

/**
 * canonical 사이드바 로고 serve delta 를 FE SidebarLogoOverride(flat {src}) 로 환원.
 * 저장 shape 가 이미 해석된 url 이므로 light url 을 src 로 flatten 한다.
 * 홈 정적 폴백(TenantUiConfig.sidebarLogo)은 light 고정 — theme-aware 소비는 AppSidebar 가
 * canonical logo 를 직접 resolveSidebarLogo 로 해석한다. logo 미설정(null) 이면 base 폴백.
 */
function mapSidebarLogo(
  logo: SpaceUiSidebarLogoResType | null,
  fallback: SidebarLogoOverride | null,
): SidebarLogoOverride | null {
  if (!logo) return fallback;
  const url = logo.variants.light.url ?? logo.variants.dark?.url;
  if (typeof url !== 'string') return fallback;
  return {
    src: url,
    alt: logo.alt ?? '',
    ...(logo.caption ? { caption: logo.caption } : {}),
  };
}
